import { Info } from "lucide-react";
import { buildFieldDiagramModel, groupFielders } from "../lib/fieldDiagram";

/**
 * A field-placement guide's diagram (issue #105).
 *
 * Reader only — Martin, 2026-09-02: "browse the preset fields from the guides
 * and see them drawn. No dragging, no saving." The editor is #587 and is
 * labelled `Future`; nothing here is built toward it.
 *
 * ALL geometry lives in `lib/fieldDiagram.js` so it can be asserted without
 * mounting anything (#615, #617). This file is presentation: it draws what
 * the model says and renders NOTHING when the model is null — which includes
 * the case of a diagram with no disclaimer.
 *
 * The disclaimer is not decoration and is not optional. Martin, 2026-08-20:
 * the per-grade close-catch restriction flag was dropped *in exchange for*
 * this sentence appearing wherever a field is drawn. Thirteen of the
 * atlas's rows are close-catching positions and junior grades restrict them,
 * so it is the only thing between this page and teaching an U11 to field at
 * silly point. Three things hold it in place, in three different layers:
 *   1. the API schema pins the exact sentence with `z.literal` — a guide
 *      without it cannot validate or be served;
 *   2. `buildFieldDiagramModel` returns null without it — no model, no dots;
 *   3. it renders unconditionally below, outside every branch.
 */

const NAVY = "#0B2E59";

/** Dot colour by depth band. Close catching is the band the disclaimer is
 * about, so it is the one that reads as a warning colour. */
const CATEGORY_COLOR = {
  close: "#B45309",
  inner_ring: NAVY,
  boundary: "#047857",
};

export default function FieldDiagram({ fieldDiagram, handedness = "right" }) {
  const model = buildFieldDiagramModel(fieldDiagram, { handedness });
  if (!model) return null;
  const groups = groupFielders(model);

  return (
    <section className="space-y-3" data-testid="field-diagram">
      <div className="flex items-center gap-2">
        <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">The field</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-4 items-start">
        <svg
          viewBox={`0 0 ${model.size} ${model.size}`}
          className="w-full max-w-[320px] h-auto mx-auto lg:mx-0"
          role="img"
          aria-label={`Field diagram: ${model.caption}`}
          data-testid="field-diagram-svg"
        >
          <title>{model.caption}</title>
          {/* The outfield, the 30-yard circle, the pitch. */}
          <circle cx={model.cx} cy={model.cy} r={model.radius} fill="#DCFCE7" stroke="#86EFAC" strokeWidth="1.5" />
          <circle
            cx={model.cx}
            cy={model.cy}
            r={model.innerCircleR}
            fill="none"
            stroke="#86EFAC"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          <rect
            x={model.pitch.x}
            y={model.pitch.y}
            width={model.pitch.width}
            height={model.pitch.height}
            fill="#E7D8B8"
            stroke="#C9B48A"
            strokeWidth="0.75"
          />
          {/* The striker, at the origin every angle and depth is measured from. */}
          <circle cx={model.cx} cy={model.cy} r="3" fill={NAVY} />

          {model.fielders.map((fielder) => (
            <g key={fielder.id} data-testid={`field-diagram-fielder-${fielder.id}`}>
              <circle cx={fielder.x} cy={fielder.y} r="8.5" fill={CATEGORY_COLOR[fielder.category] ?? NAVY} />
              <text
                x={fielder.x}
                y={fielder.y + 3.2}
                textAnchor="middle"
                fontSize="9"
                fontWeight="700"
                fill="#FFFFFF"
              >
                {fielder.number}
              </text>
            </g>
          ))}
        </svg>

        <div className="space-y-3">
          <p className="text-sm text-slate-600 leading-relaxed max-w-prose" data-testid="field-diagram-caption">
            {model.caption}
          </p>
          {groups.map((group) => (
            <div key={group.category}>
              <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">{group.label}</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {group.fielders.map((fielder) => (
                  <li key={fielder.id} className="text-sm text-slate-700 flex items-baseline gap-2">
                    <span
                      className="inline-flex items-center justify-center shrink-0 w-5 h-5 rounded-full text-[10px] font-bold text-white"
                      style={{ background: CATEGORY_COLOR[fielder.category] ?? NAVY }}
                    >
                      {fielder.number}
                    </span>
                    <span className="min-w-0">
                      {fielder.name}
                      {/* The guide's own metres, kept exact even where the dot
                          is drawn on the rope — depth is per-scenario (#105). */}
                      <span className="text-slate-400"> · {fielder.depthM}m</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Martin's grade disclaimer, 2026-08-20. Unconditional, outside every
          branch above, on every diagram. See this file's header. */}
      <div
        className="rounded-xl border-2 p-4 flex gap-3"
        style={{ borderColor: "#B45309", background: "#B4530910" }}
        data-testid="field-diagram-disclaimer"
      >
        <Info size={18} color="#B45309" className="shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700 leading-relaxed max-w-prose">{model.disclaimer}</p>
      </div>
    </section>
  );
}
